import { LinearProgress } from '@mui/material';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import { addHours } from 'date-fns';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import useTimer from '@swf/core/hooks/useTimer';
import { CashierLimitTimespan, Separator } from '@swf/first';

import { useCashierWidget } from '../../../../../../widgets/first-wallet-widget/model';
import LimitsRecordTimeFrame from '../LimitsRecordTimeframe/LimitsRecordTimeFrame';

import {
  TXTCLI_N0_left,
  TXTWCL_24hr_deposit_limitX_N0_1,
  TXTWCL_N0_day_limitX_N1,
  TXTWCL_Regulatory_limit,
  TXTWCL_The_maximum_amount_you_can_dep_ELL
} from './WalletDepositLimitsRecord.messages.json';
import style, { limitValue, progressBar, record } from './WalletDepositLimitsRecord.scss';

interface WalletDepositLimitsRecordProps {
  limitType: string;
}

const translationMap = {
  SELF_IMPOSED: {
    DAILY: TXTWCL_24hr_deposit_limitX_N0_1,
    WEEKLY: TXTWCL_N0_day_limitX_N1,
    MONTHLY: TXTWCL_N0_day_limitX_N1
  },
  REGULATORY: {
    DAILY: TXTWCL_Regulatory_limit,
    WEEKLY: TXTWCL_Regulatory_limit,
    MONTHLY: TXTWCL_Regulatory_limit
  }
};

const WalletDepositLimitsRecord: React.FC<WalletDepositLimitsRecordProps> = ({ limitType }) => {
  const { widgetSelect, widgetDispatch } = useCashierWidget();
  const { overallClosest, regulatoryLimit } = useSelector(widgetSelect.getClosestLimit);
  const isLimitRegulatory = limitType === regulatoryLimit?.limitType;
  let translationValues;

  const targetDate = overallClosest?.nextDepositDate
    ? addHours(new Date(overallClosest?.nextDepositDate), 4)
    : 0;
  const initialSecondsLeft = targetDate ? differenceInMinutes(targetDate, new Date()) * 60 : 0;
  const timer = useTimer({
    initial: initialSecondsLeft,
    modifier: (seconds) => (seconds > 0 ? seconds - 1 : 0)
  });
  const minutesLeft = Math.ceil(timer / 60);

  useEffect(() => {
    if (minutesLeft === 0) {
      widgetDispatch.fetchLimits();
    }
  }, [minutesLeft, widgetDispatch]);

  if (!overallClosest) {
    return null;
  }

  const { totalAmount, progress, remainingAmount, nextDepositDate, timespan } = overallClosest;

  if (!totalAmount) {
    return null;
  }

  const { [timespan]: limitSetTranslation } = translationMap[limitType];

  switch (timespan) {
    case CashierLimitTimespan.Daily:
      translationValues = {
        0: <CurrencyAmount value={totalAmount} />
      };
      break;
    case CashierLimitTimespan.Weekly:
      translationValues = {
        0: 7,
        1: <CurrencyAmount value={totalAmount} />
      };
      break;
    default:
      translationValues = {
        0: 30,
        1: <CurrencyAmount value={totalAmount} />
      };
  }

  return (
    <div className={record}>
      <Separator />
      <Translation tagName="h4" {...limitSetTranslation} values={translationValues} />
      {limitType === regulatoryLimit?.limitType && (
        <Translation tagName="p" {...TXTWCL_The_maximum_amount_you_can_dep_ELL} />
      )}
      <LinearProgress
        className={classNames(progressBar, {
          [style.colorRed]: progress && progress >= 100,
          [style.colorOrange]: progress && progress >= 80,
          [style.colorGreen]: progress && progress < 80
        })}
        variant="determinate"
        value={progress}
      />
      <div className={limitValue}>
        {nextDepositDate !== null && (remainingAmount ?? 0) >= 0 && minutesLeft > 0 && (
          <LimitsRecordTimeFrame totalMinutes={minutesLeft} />
        )}
        <Translation
          tagName="p"
          {...TXTCLI_N0_left}
          values={{
            0: (
              <CurrencyAmount
                value={Math.max(
                  0,
                  isLimitRegulatory ? regulatoryLimit?.remainingAmount : remainingAmount ?? 0
                )}
              />
            )
          }}
        />
      </div>
    </div>
  );
};

export default WalletDepositLimitsRecord;
